/**
 * Custom Authentication Service
 * Handles server-side password validation with Keycloak
 * Based on architecture document: Server-Side Password Validation approach
 */

import api from '../redux/api';

// Extract subdomain from current location
function getSubdomainFromLocation() {
  if (typeof window === 'undefined') return 'default';

  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // For localhost or bare domains (promotly.cloud, promotly.app), return 'default'
  // Only return subdomain for multi-part domains like max_muscle.promotly.cloud
  if (hostname.includes('localhost') || parts.length <= 2) {
    return 'default';
  }

  // For subdomains like max_muscle.promotly.cloud, return the first part
  return parts[0];
}

/**
 * Login with email and password
 * @param {string} email - User's real email
 * @param {string} password - User's password
 * @param {boolean} rememberMe - Request a long-lived ("remember me") session on this
 *   device. The backend is the single source of truth for whether the long session is
 *   actually granted (account-level users only — never sub-sellers or cashiers) and
 *   reports the outcome as `rememberMeApplied` / `sessionTtlSeconds` in the response.
 * @returns {Promise<Object>} Response with role_id, redirect_url and rememberMeApplied
 */
export const login = async (email, password, rememberMe = false) => {
  try {
    const subdomain = getSubdomainFromLocation();

    const response = await api.post('/auth/login', { email, password, rememberMe: Boolean(rememberMe) }, {
      headers: {
        'X-Subdomain': subdomain,
      },
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    // Handle error response from backend
    if (error.response && error.response.data) {
      const { success, message, messageAr } = error.response.data;

      // If backend returned a specific error message, use it
      if (!success) {
        // Check current language to determine which message to use
        const currentLanguage = localStorage.getItem('language') || 'en';
        const errorMessage = currentLanguage === 'ar' && messageAr ? messageAr : message;

        if (errorMessage) {
          throw new Error(errorMessage);
        }
      }
    }

    // If no specific error message, throw a generic error
    throw new Error('Login failed. Please check your credentials.');
  }
};

/**
 * Check current session status
 * @returns {Promise<Object>} Session data if authenticated
 */
export const checkSession = async () => {
  try {
    const response = await api.get('/auth/session', {
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error('[customAuthService.checkSession] Error:', error);
    throw error;
  }
};

/**
 * Logout current user
 * @returns {Promise<Object>} Logout confirmation
 */
export const logout = async () => {
  try {
    const response = await api.post('/auth/logout', {}, {
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error('[customAuthService.logout] Error:', error);
    throw error;
  }
};

/**
 * Request password reset
 * @param {string} email - User's email
 * @returns {Promise<Object>} Reset request confirmation
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });

    return response.data;
  } catch (error) {
    console.error('[customAuthService.requestPasswordReset] Error:', error);
    throw error;
  }
};

/**
 * Verify reset code and reset password in one step
 * @param {string} email - User's email
 * @param {string} otp - 6-digit OTP verification code
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Password reset confirmation
 */
export const verifyResetCode = async (email, otp, newPassword) => {
  try {
    const response = await api.post('/auth/verify-code', {
      email,
      otp,
      newPassword
    });

    return response.data;
  } catch (error) {
    console.error('[customAuthService.verifyResetCode] Error:', error);
    throw error;
  }
};

/**
 * Reset password with verified code
 * @deprecated Use verifyResetCode instead, which now handles both verification and password reset
 * @param {string} email - User's email
 * @param {string} code - Verified code
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Reset confirmation
 */
export const resetPassword = async (email, code, newPassword) => {
  try {
    const response = await api.post('/auth/reset-password', { email, code, newPassword });

    return response.data;
  } catch (error) {
    console.error('[customAuthService.resetPassword] Error:', error);
    throw error;
  }
};

// Mock service for development/testing
export const mockAuthService = {
  login: async (email, password, rememberMe = false) => {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock validation
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    // Mock different user roles based on email
    let role_id = 3; // Default: Seller
    let redirect_url = '/seller/dashboard';

    if (email.includes('admin')) {
      role_id = 2;
      redirect_url = '/account/dashboard';
    } else if (email.includes('cashier')) {
      role_id = 4;
      redirect_url = '/cashier/pos';
    }

    // Mirror the backend contract: the long session belongs to whoever
    // OPERATES the surface they sign in from — an account admin on the account
    // subdomain, or a shop owner on their own shop. A cashier never gets it
    // (their session is governed by the shift window). The mock has no
    // subdomain context, so it mirrors the role half of the rule.
    const rememberMeApplied = Boolean(rememberMe) && (role_id === 2 || role_id === 3);
    const sessionTtlSeconds = rememberMeApplied ? 2592000 : 86400;

    // Mock successful response
    return {
      success: true,
      message: 'Login successful',
      role_id,
      redirect_url,
      rememberMeApplied,
      sessionTtlSeconds,
      user: {
        id: 1,
        email,
        first_name: 'John',
        last_name: 'Doe',
        role_id,
        account_id: 1,
      },
      ...(role_id === 3 && {
        seller: {
          id: 1,
          business_name: 'Acme Store',
        },
      }),
    };
  },

  checkSession: async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    throw new Error('No active session');
  },

  logout: async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return { success: true, message: 'Logged out successfully' };
  },
};

// Export the service to use
const customAuthService = {
  login,
  checkSession,
  logout,
  requestPasswordReset,
  verifyResetCode,
  resetPassword,
};

export default customAuthService;